import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ColorMode, Theme, ThemePreference } from '@itr/shared';
import { BUILTIN_THEMES, themeToTokens } from '@itr/shared';
import { api } from '../api/client';

interface ThemeContextValue {
  theme: Theme;
  themes: Theme[];
  preference: ThemePreference;
  /** The mode actually in effect, after resolving `system`. */
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ColorMode) => void;
  setThemeId: (id: string | null) => void;
  setPreference: (patch: Partial<ThemePreference>) => void;
  reloadThemes: () => Promise<void>;
}

const DEFAULT_PREFERENCE: ThemePreference = {
  themeId: null,
  mode: 'system',
  highContrast: null,
  reduceMotion: null,
  density: null,
  fontScale: null,
};

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = 'itr.theme';

function loadStoredPreference(): ThemePreference {
  try {
    return { ...DEFAULT_PREFERENCE, ...JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') };
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themes, setThemes] = useState<Theme[]>(BUILTIN_THEMES);
  const [preference, setPreferenceState] = useState<ThemePreference>(loadStoredPreference);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  // Follow the OS while the user is on `system` — including a change made while
  // the tab is open, which is common on a scheduled light/dark switch.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolvedMode: 'light' | 'dark' =
    preference.mode === 'system' ? (systemDark ? 'dark' : 'light') : preference.mode;

  const theme = useMemo(() => {
    return themes.find((t) => t.id === preference.themeId) ?? themes.find((t) => t.isDefault) ?? themes[0]!;
  }, [themes, preference.themeId]);

  // Push tokens onto :root. Doing this imperatively rather than through a styled
  // wrapper means portals, the scrollbar and print styles all inherit the theme.
  useEffect(() => {
    const root = document.documentElement;
    const tokens = themeToTokens(theme, resolvedMode, preference);
    for (const [key, value] of Object.entries(tokens)) root.style.setProperty(key, value);

    root.dataset.mode = resolvedMode;
    root.dataset.theme = theme.id;
    root.style.background = resolvedMode === 'dark' ? theme.dark.bg : theme.light.bg;

    const meta = document.querySelector('meta[name="theme-color"]:not([media])') ?? createThemeColorMeta();
    meta.setAttribute('content', resolvedMode === 'dark' ? theme.dark.bg : theme.light.bg);

    document.title = theme.branding.productName;
  }, [theme, resolvedMode, preference]);

  const persist = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    // Best-effort server sync so the choice follows the user to another device.
    // A failure here is not worth surfacing — the local change already applied.
    void api.put('/api/me/theme-preference', next).catch(() => {});
  }, []);

  const reloadThemes = useCallback(async () => {
    try {
      const [list, me] = await Promise.all([
        api.get<Theme[]>('/api/themes'),
        api.get<{ themePreference: ThemePreference }>('/api/auth/me').catch(() => null),
      ]);
      if (list.length > 0) setThemes(list);
      if (me?.themePreference) {
        // The server is authoritative on first load, but never clobbers a mode
        // the user just picked in this tab.
        setPreferenceState((current) => ({ ...me.themePreference, mode: current.mode ?? me.themePreference.mode }));
      }
    } catch {
      /* stay on the built-ins — the app must render even if the API is down */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      themes,
      preference,
      resolvedMode,
      setMode: (mode) => persist({ ...preference, mode }),
      setThemeId: (themeId) => persist({ ...preference, themeId }),
      setPreference: (patch) => persist({ ...preference, ...patch }),
      reloadThemes,
    }),
    [theme, themes, preference, resolvedMode, persist, reloadThemes],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

/** Series colours for charts, in the theme's fixed slot order. */
export function useChartColors(): string[] {
  const { theme, resolvedMode } = useTheme();
  return resolvedMode === 'dark' ? theme.dark.chart : theme.light.chart;
}

function createThemeColorMeta(): HTMLMetaElement {
  const meta = document.createElement('meta');
  meta.name = 'theme-color';
  document.head.appendChild(meta);
  return meta;
}
